import React, { useRef, useState } from 'react';
import { Button, Input, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';

// Column header search dropdown shared by the Warehouse tables.
export function useColumnSearch() {
    const [tableSearchText, setTableSearchText] = useState('');
    const [tableSearchedColumn, setTableSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const handleTableSearch = (selectedKeys, dataIndex, confirm) => {
        confirm();
        setTableSearchText(selectedKeys[0]);
        setTableSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters, confirm) => {
        clearFilters();
        confirm();
        setTableSearchText('');
        setTableSearchedColumn('');
    };

    // cellMinWidth floors the column by wrapping cell content in a block div.
    // It is the only column floor Firefox honors: Gecko ignores min-width on
    // th/td and on col, so column width/minWidth props only floor Chromium.
    // Pass the desired column width minus the cell's horizontal padding
    // (32px at the default table size).
    const getColumnSearchProps = (dataIndex, friendlySearch, cellMinWidth) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${friendlySearch}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value
                        ? [e.target.value]
                        : [])}
                    onPressEnter={() => handleTableSearch(selectedKeys, dataIndex, confirm)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleTableSearch(selectedKeys, dataIndex, confirm)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters, confirm)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: (filtered) => (
            <SearchOutlined style={{
                color: filtered
                    ? '#1677ff'
                    : undefined,
            }} />
        ),

        onFilter: (value, record) =>
            String(record[dataIndex] ?? '')
                .toLowerCase()
                .includes(String(value).toLowerCase()),

        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },

        render: (text) => {
            const content = tableSearchedColumn === dataIndex
                ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[tableSearchText]}
                        autoEscape
                        textToHighlight={text
                            ? text.toString()
                            : ''}
                    />
                )
                : (
                    text
                );
            return cellMinWidth
                ? <div style={{ minWidth: cellMinWidth }}>{content}</div>
                : content;
        },
    });

    const resetColumnSearch = () => {
        setTableSearchText('');
        setTableSearchedColumn('');
    };

    return { getColumnSearchProps, tableSearchText, tableSearchedColumn, resetColumnSearch };
}
